/**
 * Utilitários de SEO
 *
 * Funções auxiliares para melhorar SEO do site
 */

import { getProductFullUrl, getPublicSiteUrl } from "./productUrl";

/**
 * Gera meta tags básicas para uma página
 */
export const generateMetaTags = (
  title,
  description,
  image,
  url,
  type = "website"
) => {
  const siteName = "Emperc — Placas de TV testadas";
  const defaultDescription =
    "Emperc (Em Perfeitas Condições): placas e fontes de TV usadas, testadas em bancada, com garantia de funcionamento para técnicos.";
  const siteUrl = getPublicSiteUrl();
  const fullTitle = title ? `${title} | ${siteName}` : siteName;

  // URL completa com protocolo e domínio
  let fullUrl = url;
  if (!fullUrl) {
    fullUrl = typeof window !== "undefined" ? window.location.href : siteUrl;
  } else if (!fullUrl.startsWith("http")) {
    // Se for uma URL relativa, adiciona o domínio
    fullUrl = `${siteUrl}${fullUrl.startsWith("/") ? "" : "/"}${fullUrl}`;
  }

  // URL canônica (sem query params para evitar duplicação)
  const canonicalUrl = fullUrl.split("?")[0].split("#")[0];

  // Imagem padrão ou fornecida
  const defaultImage = `${siteUrl}/logo.png`;
  const ogImage = image || defaultImage;
  // Garantir que a imagem tenha URL absoluta
  const absoluteImage = ogImage.startsWith("http")
    ? ogImage
    : `${siteUrl}${ogImage.startsWith("/") ? "" : "/"}${ogImage}`;

  return {
    title: fullTitle,
    description: description || defaultDescription,
    canonical: canonicalUrl,
    openGraph: {
      title: fullTitle,
      description: description || defaultDescription,
      image: absoluteImage,
      url: canonicalUrl,
      type,
      siteName,
      locale: "pt_BR",
    },
    twitter: {
      card: "summary_large_image",
      title: fullTitle,
      description: description || defaultDescription,
      image: absoluteImage,
      site: "@emperc",
      creator: "@emperc",
    },
  };
};

/**
 * Gera structured data para produto
 * Inclui reviews individuais se disponíveis
 */
export const generateProductStructuredData = (product, reviews = []) => {
  if (!product) return null;

  const siteUrl =
    typeof window !== "undefined"
      ? window.location.origin
      : "https://www.emperc.com.br";
  const productUrl = getProductFullUrl(product);

  // Garantir que imagens sejam URLs absolutas
  const images =
    product.images?.length > 0
      ? product.images.map((img) =>
          img.startsWith("http")
            ? img
            : `${siteUrl}/${img}`.replace(/([^:]\/)\/+/g, "$1")
        )
      : product.image
      ? [
          product.image.startsWith("http")
            ? product.image
            : `${siteUrl}/${product.image}`.replace(/([^:]\/)\/+/g, "$1"),
        ]
      : [];

  const structuredData = {
    "@context": "https://schema.org/",
    "@type": "Product",
    name: product.name,
    image: images,
    description:
      product.seoDescription?.trim() ||
      product.description?.slice(0, 160) ||
      `${product.name} - ${product.brand || "Produto"} disponível na Emperc`,
    sku: product._id,
    mpn: product.sku || product._id,
    brand: {
      "@type": "Brand",
      name: product.brand || "Emperc",
    },
    category: product.category?.name || "Produtos",
    offers: {
      "@type": "Offer",
      url: productUrl,
      priceCurrency: "BRL",
      price: product.price,
      priceValidUntil: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      availability:
        product.stock > 0
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
      itemCondition: "https://schema.org/NewCondition",
      seller: {
        "@type": "Organization",
        name: "Emperc",
      },
    },
  };

  // Adicionar aggregateRating se houver rating
  if (product.rating && product.numReviews > 0) {
    structuredData.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: product.rating,
      reviewCount: product.numReviews,
      bestRating: "5",
      worstRating: "1",
    };
  }

  // Adicionar reviews individuais se disponíveis
  if (reviews && reviews.length > 0) {
    structuredData.review = reviews.map((review) => ({
      "@type": "Review",
      author: {
        "@type": "Person",
        name: review.user?.name || "Cliente Emperc",
      },
      datePublished: review.createdAt
        ? new Date(review.createdAt).toISOString()
        : new Date().toISOString(),
      reviewBody: review.comment || "",
      reviewRating: {
        "@type": "Rating",
        ratingValue: review.rating || 0,
        bestRating: "5",
        worstRating: "1",
      },
    }));
  }

  return structuredData;
};

/**
 * Gera structured data para organização
 */
export const generateOrganizationStructuredData = () => {
  return {
    "@context": "https://schema.org/",
    "@type": "Organization",
    name: "Emperc",
    url: typeof window !== "undefined" ? window.location.origin : "",
    logo: `${process.env.REACT_APP_BACKEND_URL || ""}/logo.png`,
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+55-21-99838-2460",
      contactType: "customer service",
      areaServed: "BR",
      availableLanguage: ["Portuguese", "English"],
    },
    sameAs: ["https://wa.me/5521998382460"],
  };
};

/**
 * Gera structured data para breadcrumbs
 */
export const generateBreadcrumbStructuredData = (items) => {
  if (!items || items.length === 0) return null;

  return {
    "@context": "https://schema.org/",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item:
        typeof window !== "undefined"
          ? `${window.location.origin}${item.url}`
          : item.url,
    })),
  };
};

/**
 * Gera structured data para coleção de produtos
 */
export const generateCollectionStructuredData = (
  products,
  collectionName = "Produtos"
) => {
  if (!products || products.length === 0) return null;

  return {
    "@context": "https://schema.org/",
    "@type": "CollectionPage",
    name: collectionName,
    description: `Catálogo de ${collectionName} — placas e peças de TV testadas na Emperc`,
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: products.length,
      itemListElement: products.map((product, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "Product",
          name: product.name,
          image: product.image || product.images?.[0],
          offers: {
            "@type": "Offer",
            priceCurrency: "BRL",
            price: product.price,
            availability:
              product.stock > 0
                ? "https://schema.org/InStock"
                : "https://schema.org/OutOfStock",
          },
        },
      })),
    },
  };
};

/**
 * Gera structured data para FAQ (Perguntas Frequentes)
 * @param {Array} faqs - Array de objetos { question: string, answer: string }
 */
export const generateFAQStructuredData = (faqs) => {
  if (!faqs || faqs.length === 0) return null;

  return {
    "@context": "https://schema.org/",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
};

/**
 * Gera structured data para vídeo
 * @param {Object} video - Objeto com informações do vídeo
 */
export const generateVideoStructuredData = (video) => {
  if (!video) return null;

  const siteUrl =
    typeof window !== "undefined"
      ? window.location.origin
      : "https://www.emperc.com.br";

  return {
    "@context": "https://schema.org/",
    "@type": "VideoObject",
    name: video.name || "Vídeo do Produto",
    description: video.description || "",
    thumbnailUrl: video.thumbnailUrl || `${siteUrl}/logo.png`,
    uploadDate: video.uploadDate || new Date().toISOString(),
    duration: video.duration || "PT1M", // Formato ISO 8601 (ex: PT1M30S = 1 minuto e 30 segundos)
    contentUrl: video.contentUrl || "",
    embedUrl: video.embedUrl || video.contentUrl || "",
    ...(video.publisher
      ? {
          publisher: {
            "@type": "Organization",
            name: video.publisher.name || "Emperc",
            logo: {
              "@type": "ImageObject",
              url: video.publisher.logo || `${siteUrl}/logo.png`,
            },
          },
        }
      : {}),
  };
};

/**
 * Gera structured data para HowTo (Tutoriais/Instruções)
 * @param {Object} howToData - Dados do tutorial
 */
export const generateHowToStructuredData = (howToData) => {
  if (!howToData) return null;

  const siteUrl =
    typeof window !== "undefined"
      ? window.location.origin
      : "https://www.emperc.com.br";

  return {
    "@context": "https://schema.org/",
    "@type": "HowTo",
    name: howToData.name || "Tutorial",
    description: howToData.description || "",
    image: howToData.image || `${siteUrl}/logo.png`,
    totalTime: howToData.totalTime || "PT10M", // Formato ISO 8601
    estimatedCost: {
      "@type": "MonetaryAmount",
      currency: "BRL",
      value: howToData.estimatedCost || "0",
    },
    tool:
      howToData.tools?.map((tool) => ({
        "@type": "HowToTool",
        name: tool,
      })) || [],
    step:
      howToData.steps?.map((step, index) => ({
        "@type": "HowToStep",
        position: index + 1,
        name: step.name || `Passo ${index + 1}`,
        text: step.text || "",
        image: step.image || null,
        url: step.url || null,
      })) || [],
  };
};

/**
 * Gera structured data para LocalBusiness (Loja Física)
 * @param {Object} businessInfo - Informações da loja
 */
export const generateLocalBusinessStructuredData = (businessInfo = {}) => {
  const siteUrl =
    typeof window !== "undefined"
      ? window.location.origin
      : "https://www.emperc.com.br";

  // Valores padrão
  const defaultInfo = {
    name: "Emperc",
    description:
      "Emperc (Em Perfeitas Condições): placas e fontes de TV usadas, testadas em bancada, para técnicos.",
    address: {
      streetAddress:
        businessInfo.address?.streetAddress || "Endereço não informado",
      addressLocality:
        businessInfo.address?.addressLocality || "Rio de Janeiro",
      addressRegion: businessInfo.address?.addressRegion || "RJ",
      postalCode: businessInfo.address?.postalCode || "",
      addressCountry: businessInfo.address?.addressCountry || "BR",
    },
    telephone: businessInfo.telephone || "+55-21-99838-2460",
    email: businessInfo.email || "contato@emperc.com.br",
    url: siteUrl,
    priceRange: businessInfo.priceRange || "$$",
    openingHours: businessInfo.openingHours || [
      "Mo-Fr 09:00-18:00",
      "Sa 09:00-13:00",
    ],
    geo: businessInfo.geo || {
      latitude: businessInfo.latitude || -22.9068,
      longitude: businessInfo.longitude || -43.1729,
    },
  };

  return {
    "@context": "https://schema.org/",
    "@type": "LocalBusiness",
    "@id": `${siteUrl}#organization`,
    name: defaultInfo.name,
    description: defaultInfo.description,
    url: defaultInfo.url,
    logo: `${siteUrl}/logo.png`,
    image: `${siteUrl}/logo.png`,
    telephone: defaultInfo.telephone,
    email: defaultInfo.email,
    priceRange: defaultInfo.priceRange,
    address: {
      "@type": "PostalAddress",
      streetAddress: defaultInfo.address.streetAddress,
      addressLocality: defaultInfo.address.addressLocality,
      addressRegion: defaultInfo.address.addressRegion,
      postalCode: defaultInfo.address.postalCode,
      addressCountry: defaultInfo.address.addressCountry,
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: defaultInfo.geo.latitude,
      longitude: defaultInfo.geo.longitude,
    },
    openingHoursSpecification: defaultInfo.openingHours.map((hours) => {
      const [days, timeRange] = hours.split(" ");
      const [openTime, closeTime] = timeRange.split("-");

      let dayOfWeek = [];
      if (days.includes("Mo")) dayOfWeek.push("Monday");
      if (days.includes("Tu")) dayOfWeek.push("Tuesday");
      if (days.includes("We")) dayOfWeek.push("Wednesday");
      if (days.includes("Th")) dayOfWeek.push("Thursday");
      if (days.includes("Fr")) dayOfWeek.push("Friday");
      if (days.includes("Sa")) dayOfWeek.push("Saturday");
      if (days.includes("Su")) dayOfWeek.push("Sunday");

      return {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: dayOfWeek,
        opens: openTime,
        closes: closeTime,
      };
    }),
    sameAs: ["https://wa.me/5521998382460"],
  };
};
